import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { patchCrmClientContact, patchCrmOrderContact } from '../../../api';
import type { CrmParsedContact } from '../../../lib/crmContact';

export type CrmClientContactValues = CrmParsedContact & {
  delivery_address?: string | null;
};

type Props = {
  initial: CrmClientContactValues;
  userId: number;
  orderId?: number | null;
  syncOrderId?: number | null;
  canEdit: boolean;
  hapticSelection: () => void;
  hapticSuccess?: () => void;
  onSaved?: (contact: CrmClientContactValues) => void;
  compact?: boolean;
};

export function CrmClientContactEditor({
  initial,
  userId,
  orderId,
  syncOrderId,
  canEdit,
  hapticSelection,
  hapticSuccess,
  onSaved,
  compact = false,
}: Props) {
  const [name, setName] = useState(initial.name || '');
  const [phone, setPhone] = useState(initial.phone || '');
  const [company, setCompany] = useState(initial.company || '');
  const [address, setAddress] = useState(initial.delivery_address || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(initial.name || '');
    setPhone(initial.phone || '');
    setCompany(initial.company || '');
    setAddress(initial.delivery_address || '');
  }, [initial.name, initial.phone, initial.company, initial.delivery_address]);

  if (!canEdit) {
    return (
      <div className="space-y-1.5 text-xs" style={{ color: 'var(--text-2)' }}>
        {initial.name && <p>ФИО: {initial.name}</p>}
        {initial.phone && <p>Тел.: {initial.phone}</p>}
        {initial.company && <p>Компания: {initial.company}</p>}
        {initial.delivery_address && <p>Адрес: {initial.delivery_address}</p>}
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    setError(null);
    hapticSelection();
    try {
      const payload = {
        name: name.trim() || null,
        phone: phone.trim() || null,
        company: company.trim() || null,
        delivery_address: address.trim() || null,
        sync_order_id: syncOrderId ?? orderId ?? undefined,
      };
      const res = orderId
        ? await patchCrmOrderContact(orderId, payload)
        : await patchCrmClientContact(userId, payload);
      const cc = res.client_contact;
      const next: CrmClientContactValues = {
        name: cc.name ?? null,
        phone: (cc.phone as string | null) ?? null,
        company: (cc.company as string | null) ?? null,
        delivery_address: ((cc.delivery_address as string | null) ?? address.trim()) || null,
      };
      onSaved?.(next);
      hapticSuccess?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const fieldClass = compact ? 'input-glass w-full text-xs min-h-[40px]' : 'input-glass w-full text-sm min-h-[44px]';

  return (
    <div className="space-y-2">
      <label className="block">
        <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
          ФИО / контакт
        </span>
        <input className={`${fieldClass} mt-1`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Иван Иванов" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
          Телефон
        </span>
        <input
          className={`${fieldClass} mt-1`}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+7 …"
          inputMode="tel"
        />
      </label>
      {!compact && (
        <label className="block">
          <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
            Компания
          </span>
          <input className={`${fieldClass} mt-1`} value={company} onChange={(e) => setCompany(e.target.value)} placeholder="ООО …" />
        </label>
      )}
      <label className="block">
        <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
          Адрес объекта
        </span>
        <textarea
          className={`${fieldClass} mt-1 min-h-[72px] resize-y`}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Доставка / монтаж"
        />
      </label>
      {error && (
        <p className="text-[10px]" style={{ color: '#FC814A' }}>
          {error}
        </p>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => void save()}
        className="press w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px] disabled:opacity-50"
        style={{ background: 'rgba(104,211,145,0.15)', color: '#68D391', border: '1px solid rgba(104,211,145,0.35)' }}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Сохранить контакты
      </button>
      <p className="text-[10px] leading-snug" style={{ color: 'var(--text-3)' }}>
        Сохраняется в профиль клиента и в активную сделку ONV — подставится в КП и доставку.
      </p>
    </div>
  );
}
